const get = require('lodash.get');

/*
Create an application change history that is formatted to enable
display in the UI.
*/

const _private = {
    getObjectName,
    ignoredForPatchHistory,
    toPrimitives
};

module.exports = {
    _private,
    getDeletePatches,
    getPatchesByOperations,
    getPatchHistory,
    isPatchForCollectionItem,
    toDot
};

/**
 * expand any object values to separate patches,
 * so each patch is for a primitive value
 * then for each patch, add the old value
 */
function getPatchHistory(document, patches) {
    return flatten(
        flatten(patches.filter(patch => !ignoredForPatchHistory(patch)).map(toPrimitives))
            .filter(patch => !ignoredForPatchHistory(patch))
            .map(patch => {
                let oldValue = get(document, toDot(patch.path));
                if (typeof oldValue !== 'undefined') {
                    if (patch.op === 'remove') {
                        //the primitivation of value won't have given us anything. need to do it on the oldvalue
                        return toPrimitives({ ...patch, value: oldValue }).map(p => ({
                            op: p.op,
                            path: p.path,
                            oldValue: p.value
                        }));
                    }
                    return { ...patch, oldValue };
                }
                return { ...patch };
            })
    ).flatMap(patch => changeReplaceWithEmptyToRemove(patch));
}

function changeReplaceWithEmptyToRemove(patch) {
    if (patch.op !== 'replace') return patch;
    if (['{}', ''].includes(JSON.stringify(patch.value))) {
        return toPrimitives({ op: 'remove', path: patch.path, value: patch.oldValue });
    }
    return patch;
}

function getDeletePatches(patches) {
    const removePatches = getPatchesByOperations(patches, ['remove']);
    return removePatches
        .filter(patch => {
            return isPatchForCollectionItem(patch);
        })
        .map(patch => {
            let removeTest = patches.find(p => p.op === 'test' && p.path === patch.path);
            return {
                op: 'delete',
                array: getObjectName(patch),
                path: (removeTest && removeTest.path) || patch.path,
                value: null
            };
        });
}

function getObjectName(patch) {
    const segments = getPatchSegments(patch);
    if (patch.path.startsWith('/')) {
        return segments[1];
    }
    return segments[0];
}

//true if the patch path ends with a number, like /items/3 or /tenant/0/app/0
function isPatchForCollectionItem(patch) {
    const segments = getPatchSegments(patch);
    if (segments.length === 0) return false;

    const lastSegment = segments[segments.length - 1];
    return isInteger(lastSegment) || lastSegment === '-';
}

function getPatchSegments(patch) {
    if (!patch) {
        throw new Error('Patch is required.');
    }
    if (!patch.path) {
        throw new Error('Patch path is required.');
    }
    return patch.path.split('/');
}

function getPatchesByOperations(patches, operations) {
    if (!patches) {
        throw new Error('Patches are required');
    }
    return patches.filter(x => operations.includes(x.op));
}

// Removing leading slash and convert remainder to dots
function toDot(path) {
    return path.replace(/\//g, '.').replace(/^\./, '');
}

function flatten(array) {
    return array.reduce((prev, curr) => prev.concat(curr), []);
}

function toPrimitives(patch) {
    if (
        //if we have no value, or
        !patch.value ||
        //if it is not an object, or
        typeof patch.value !== 'object' ||
        //if the object has not properties, or
        !Object.keys(patch.value).length ||
        //if it is an ObjectId
        (patch.value._bsontype && patch.value._bsontype === 'ObjectID') ||
        (patch.value._bsontype && patch.value._bsontype === 'ObjectId') ||
        (patch.value.bsonType && patch.value.bsonType === 'ObjectId')
    ) {
        //then just return what we've got (as array)
        return [patch];
    }
    return flatten(
        Object.keys(patch.value).map(key =>
            toPrimitives({ op: patch.op, path: `${patch.path}/${key}`, value: patch.value[key] })
        )
    );
}

// Helper
function isInteger(value) {
    return Number.isInteger(Number.parseFloat(value));
}

/**
 * Matches against the regex that excludes patches for any pre/changed/post inventory earmarks,
 * as well as inventory:inventory or inventory:excludedInventory nested items
 * I.e. it matches any of these:
 *  { path: '/inventory/0/assetNo/pre' },
 *  { path: 'inventory.0.assetNo.post' },
 *  { path: 'inventory[0].assetNo.changed' },
 *  { path: '/inventory[0]/description/changed' },
 *  { path: '/inventory/0/inventory:inventory' },
 *  { path: '/inventory/0/description', value: { pre: 'x', changed: false, post: 'y' } },
 *  { path: '/inventory[0]', value: { 'inventory:excludedInventory': {} } },
 *  { path: 'meta.modifiedBy' },
 *  { path: '/locationModifiedTime' },
 * @param {*} patch
 * @param {string} patch.path patched path starting with "/"
 */
function ignoredForPatchHistory(patch) {
    if (!patch.path) return false; //? or DO ignore these???
    //if the patch is for inside the inventory array
    //NOTE: for some unknown reason if x.match() doesn't match, it returns null,
    //BUT that null is evaluated as thruty
    if (patch.path.match(/^\/?inventory[/[.]\d+[/.\]]/gim) != null) {
        //if the path includes inventory:xxxx ignore it
        if (patch.path.match(/^\/?inventory[/[.]\d+[/.\]]inventory:/gim) != null) return true;
        //if the path includes pre/post/changed ignore it
        if (patch.path.match(/^\/?inventory[/[.]\d+[/.\]].+[/.](pre|post|changed)/gim) != null) return true;
        //if the value passed in has properties pre/post/changed or inventory:xxx, ignore it
        if (
            patch.value &&
            Object.keys(patch.value).some(
                key => key.startsWith('inventory:') || ['pre', 'post', 'changed'].includes(key)
            )
        ) {
            return true;
        }
    }
    //ignore any patches for updates to 'meta'
    if (patch.path.match(/^\/?meta[/.]/gim) != null) return true;

    const internalFields = ['locationModifiedTime']; //maybe also /inLocation?
    //Otherwise, just ignore some internal fields
    return internalFields.reduce((sofar, field) => sofar || [`/${field}`, field].includes(patch.path), false);
}
